/**
 * `/print/snapshot/:id` — the one generic print page (#783 Slice 1).
 *
 * Loaded by the server's headless Chromium after the PDF endpoint
 * creates a snapshot. Fetches the snapshot once from the public
 * snapshot endpoint, dispatches on `definitionId@definitionVersion`
 * to the matching layout, and signals readiness to the headless
 * renderer by setting `data-print-ready="true"` on `<body>` once the
 * render commits.
 *
 * Adding a new report = a new layout file + a case in the dispatcher
 * below. The print page itself stays the same.
 *
 * Presentation only — never makes another API call beyond the one
 * snapshot fetch.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchSnapshot } from './api'
import { RenderModeProvider } from './RenderModeContext'
import { VariantQualityLayout } from './layouts/VariantQualityLayout'
import { RunDetailLayout } from './layouts/RunDetailLayout'
import { DetailedTestResultsLayout } from './layouts/DetailedTestResultsLayout'
import type {
  SnapshotEnvelope,
  RunDetailViewModel,
  VariantQualityViewModel,
  DetailedTestResultsViewModel,
} from './types'

export default function PrintSnapshotPage() {
  const { id } = useParams<{ id: string }>()
  const [snapshot, setSnapshot] = useState<SnapshotEnvelope | null>(null)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!id) {
      setError(new Error('Missing snapshot id'))
      return
    }
    let cancelled = false
    fetchSnapshot(id).then(
      (s) => {
        if (!cancelled) setSnapshot(s)
      },
      (err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)))
      },
    )
    return () => {
      cancelled = true
    }
  }, [id])

  // Signal readiness to the headless renderer once we've reached a
  // terminal state (rendered the snapshot, or rendered an error).
  // Either way the PDF reflects whatever's on screen — a render-time
  // error becomes a small "couldn't load" page rather than a hang.
  useEffect(() => {
    if (!snapshot && !error) return
    document.body.dataset.printReady = 'true'
    return () => {
      delete document.body.dataset.printReady
    }
  }, [snapshot, error])

  if (error) {
    return (
      <div className="mx-auto max-w-[8.5in] p-8 text-sm text-gray-700">
        <h1 className="text-lg font-semibold text-red-700">
          Couldn't load report
        </h1>
        <p className="mt-2">{error.message}</p>
      </div>
    )
  }
  if (!snapshot) {
    return (
      <div className="mx-auto max-w-[8.5in] p-8 text-sm text-gray-500">
        Loading…
      </div>
    )
  }
  return (
    <RenderModeProvider value="print">{dispatch(snapshot)}</RenderModeProvider>
  )
}

/** Dispatch on `definitionId@version` to the matching layout. Add a
 * case here when registering a new report type. */
function dispatch(snapshot: SnapshotEnvelope): React.ReactNode {
  if (
    snapshot.definitionId === 'variant-quality' &&
    snapshot.definitionVersion === 1
  ) {
    return (
      <VariantQualityLayout
        viewModel={snapshot.viewModel as VariantQualityViewModel}
      />
    )
  }
  if (
    snapshot.definitionId === 'run-detail' &&
    snapshot.definitionVersion === 1
  ) {
    return (
      <RunDetailLayout
        viewModel={snapshot.viewModel as RunDetailViewModel}
      />
    )
  }
  if (
    snapshot.definitionId === 'detailed-test-results' &&
    snapshot.definitionVersion === 1
  ) {
    return (
      <DetailedTestResultsLayout
        viewModel={snapshot.viewModel as DetailedTestResultsViewModel}
      />
    )
  }
  return (
    <div className="mx-auto max-w-[8.5in] p-8 text-sm text-gray-700">
      <h1 className="text-lg font-semibold">Unsupported report</h1>
      <p className="mt-2">
        No renderer registered for{' '}
        <code className="font-mono">
          {snapshot.definitionId}@v{snapshot.definitionVersion}
        </code>
        .
      </p>
    </div>
  )
}
